import { useQuery } from "@tanstack/react-query";
import { FC, ReactElement, ReactNode, createContext, useContext } from "react";

import { AuthProfile } from "@nens/threedi/openapi-client";

import { getAuthApi } from "../apiConfiguration";

// Note the hack with null as AuthProfile is acceptable because the context will only be
// supplied by the Provider in situations where profile is an AuthProfile, not null.
// Saves a lot of '!' use everywhere the context is actually used.
export const AuthContext = createContext<{ profile: AuthProfile }>({
  profile: null as unknown as AuthProfile,
});

interface AuthProviderProps {
  loginUrl: string;
  fetchingComponent?: ReactElement;
  children: ReactNode;
}

const AuthProvider: FC<AuthProviderProps> = ({ loginUrl, fetchingComponent = null, children }) => {
  const { status, data } = useQuery({
    queryKey: ["auth"],
    queryFn: async () => {
      const authApi = getAuthApi();
      return await authApi.authProfileList();
    },
  });

  if (status === "error") {
    window.location.replace(loginUrl);
    return null;
  }

  if (status === "pending" || !data) {
    return fetchingComponent;
  }

  if (status === "success") {
    return <AuthContext.Provider value={{ profile: data }}>{children}</AuthContext.Provider>;
  }

  // Should never happen.
  return <p>Failed to load Rana HCC.</p>;
};

// Helper hook to get access to user from anywhere in the children tree of AuthContext.Provider.
export function useUser() {
  const context = useContext(AuthContext);
  return context && context.profile;
}

export default AuthProvider;
